import { inject, Injectable } from "@angular/core";
import { PopupService } from "src/app/services/popup/popup.service";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { defer, forkJoin, iif, lastValueFrom, map, of, switchMap } from "rxjs";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import {
  ConnectIntegrationConnectionResult,
  ConnectIntegrationConnectionResultType,
  ConnectIntegrationConnector,
  ConnectIntegrationInfo
} from "src/app/modules/bootstrap/functions/integrations/connect-integration/connect-integration.connector";
import { Connection } from "src/app/backend/models/connection";

@Injectable()
export class ConnectIntegrationBasicConnector implements ConnectIntegrationConnector {
  private readonly popupService = inject(PopupService);
  private readonly installationBackend = inject(InstallationBackend);

  connect({
    integration,
    installation,
    isRefresh,
    refreshConnectionId,
    phase
  }: ConnectIntegrationInfo): Promise<ConnectIntegrationConnectionResult> {
    return new Promise<ConnectIntegrationConnectionResult>(async (resolve) => {
      await this.popupService.createEntity<{
        username: string | null;
        password: string | null;
      }>({
        title: sntTranslate(marker("Inserisci le credenziali")),
        initialFormData: {
          username: null,
          password: null
        },
        modalConfig: {
          dialog: true,
          height: 256,
          icon: "fa-light fa-puzzle-piece-simple"
        },
        formItems: [
          {
            dataField: "username",
            label: {
              text: sntTranslate(marker("Username")),
              location: "top",
              showColon: false
            },
            validationRules: [{ type: "required" }]
          },
          {
            dataField: "password",
            label: {
              text: sntTranslate(marker("Password")),
              location: "top",
              showColon: false
            },
            validationRules: [{ type: "required" }]
          }
        ],
        create: async (data) => {
          const connection = await lastValueFrom(
            forkJoin({
              createdConnetion: iif(
                () => !isRefresh,
                defer(() => {
                  return this.installationBackend.createConnection({
                    provider: integration.provider,
                    type: "basic",
                    credentials: {
                      username: data.username!,
                      password: data.password!
                    },
                    default: phase === "install",
                    installationId: installation.id
                  });
                }),
                of(undefined)
              ),
              updatedConnection: iif(
                () => !!isRefresh,
                defer(() => {
                  return this.installationBackend
                    .loadConnectionById(refreshConnectionId!)
                    .pipe(
                      switchMap((connection: Connection) => {
                        return this.installationBackend
                          .updateConnectionCredentials({
                            connectionId: refreshConnectionId!,
                            version: connection.version,
                            type: "basic",
                            credentials: {
                              username: data.username!,
                              password: data.password!
                            }
                          })
                          .pipe(
                            switchMap(() =>
                              this.installationBackend.loadConnectionById(
                                refreshConnectionId!
                              )
                            )
                          );
                      })
                    );
                }),
                of(undefined)
              )
            }).pipe(
              map(({ createdConnetion, updatedConnection }) => {
                return updatedConnection || createdConnetion;
              })
            )
          );

          resolve({
            result: ConnectIntegrationConnectionResultType.SUCCEED,
            connection
          });
        },
        onCancel: async () => {
          resolve({
            result: ConnectIntegrationConnectionResultType.CANCELED
          });
        }
      });
    });
  }
}
